import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from './AppIcon';
import { PulseBeamBorder } from './ui/PulseBeamBorder';

const PROMPT_DELAY = 1000; // 1 second
const REAPPEAR_DELAY = 5000; // 5 seconds

const SignUpPrompt = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // If user is logged in, don't show
        if (user) return;

        // Initial timer to show
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, PROMPT_DELAY);

        return () => clearTimeout(timer);
    }, [user]);

    const handleDismiss = () => {
        setIsVisible(false);
        // Persistently reappear after 5 seconds
        setTimeout(() => {
            if (!user) {
                setIsVisible(true);
            }
        }, REAPPEAR_DELAY);
    };

    const handleSignUp = () => {
        setIsVisible(false);
        navigate('/job-seeker-registration-login');
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <div className="fixed bottom-6 right-6 z-1050">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="w-full max-w-sm"
                    >
                        <PulseBeamBorder className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden">
                            <div className="p-6 relative">
                                <button
                                    onClick={handleDismiss}
                                    className="absolute top-2 right-2 p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                                >
                                    <Icon name="X" size={16} />
                                </button>

                                <div className="flex items-start gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400 shrink-0">
                                        <Icon name="Rocket" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-neutral-900 dark:text-white">
                                            Unlock Your Potential
                                        </h3>
                                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                                            Join GradOne to track applications, get AI-powered feedback, and land your dream job faster.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={handleDismiss}
                                        className="flex-1 px-4 py-2.5 text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                                    >
                                        Maybe Later
                                    </button>
                                    <button
                                        onClick={handleSignUp}
                                        className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
                                    >
                                        Sign Up Now
                                    </button>
                                </div>
                            </div>
                        </PulseBeamBorder>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default SignUpPrompt;
